import React from 'react';
import {
  Box,
  Heading,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { FaComments } from 'react-icons/fa';
import { useGetExpertChatTickets } from '../../utils/zendesk.api';

const ExpertChatLogsCard = ({ vehicleId }) => {
  const bg = useColorModeValue('white', 'gray.800');
  
  // Fetch expert chat tickets
  const { data: ticketsData, isLoading, error } = useGetExpertChatTickets(vehicleId);
  const expertChatLogs = ticketsData?.tickets || [];

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'open' || statusLower === 'pending') {
      return 'yellow';
    } else if (statusLower === 'resolved' || statusLower === 'closed') {
      return 'green';
    } else if (statusLower === 'in progress') {
      return 'blue';
    }
    return 'gray';
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box
        borderWidth="1px"
        borderColor="gray.300"
        borderRadius="md"
        overflow="hidden"
        bg={bg}
        boxShadow="sm"
      >
        <Flex
          justify="space-between"
          align="center"
          bg="#212e89"
          color="white"
          px={4}
          py={2}
        >
          <Flex align="center" gap={2}>
            <Heading size={{ base: "md", md: "lg" }} fontWeight="semibold">
              Expert Chat Logs
            </Heading>
            <Icon as={FaComments} boxSize={8} />
          </Flex>
        </Flex>
        <Box p={6} display="flex" justifyContent="center" alignItems="center" minH="200px">
          <Spinner size="xl" color="blue.500" />
        </Box>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        borderWidth="1px"
        borderColor="gray.300"
        borderRadius="md"
        overflow="hidden"
        bg={bg}
        boxShadow="sm"
      >
        <Flex
          justify="space-between"
          align="center"
          bg="#212e89"
          color="white"
          px={4}
          py={2}
        >
          <Flex align="center" gap={4}>
            <Heading size={{ base: "md", md: "lg" }} fontWeight="semibold">
              Expert Chat Logs
            </Heading>
            <Icon as={FaComments} boxSize={8} />
          </Flex>
        </Flex>
        <Box p={6}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text>Failed to load expert chat logs: {error.message}</Text>
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      borderWidth="1px"
      borderColor="gray.300"
      borderRadius="md"
      overflow="hidden"
      bg={bg}
      boxShadow="sm"
    >
      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        bg="#212e89"
        color="white"
        px={4}
        py={2}
      >
        <Flex align="center" gap={4}>
          <Heading size={{ base: "md", md: "lg" }} fontWeight="semibold">
            Expert Chat Logs
          </Heading>
          <Icon as={FaComments} boxSize={8} />
        </Flex>
      </Flex>

      <Box p={6}>
        {/* Table */}
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr bg="gray.100">
                <Th color="gray.700" fontWeight="bold" fontSize="sm">
                  Ticket ID
                </Th>
                <Th color="gray.700" fontWeight="bold" fontSize="sm">
                  Date
                </Th>
                <Th color="gray.700" fontWeight="bold" fontSize="sm">
                  Status
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {expertChatLogs.length === 0 ? (
                <Tr>
                  <Td colSpan={3} textAlign="center" py={8} color="gray.500">
                    <Text>No expert chat logs yet.</Text>
                  </Td>
                </Tr>
              ) : (
                expertChatLogs.map((log) => (
                  <Tr key={log.id || log.ticket_id} _hover={{ bg: 'gray.50' }}>
                    <Td fontWeight="medium" color="blue.600">
                      {log.ticket_id || '-'}
                    </Td>
                    <Td>{formatDate(log.created_at)}</Td>
                    <Td>
                      <Badge
                        colorScheme={getStatusColor(log.status)}
                        fontSize="xs"
                        px={2}
                        py={1}
                        borderRadius="md"
                      >
                        {log.status || 'Unknown'}
                      </Badge>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ExpertChatLogsCard;

